import { z } from 'zod';

/**
 * The emails the store sends. The code decides when each goes out; staff can switch each one on or off, send a test and
 * preview them under System > Emails. (Order messages over WhatsApp are separate: see notifications.ts.)
 */
export const EmailKind = {
  NEWSLETTER_WELCOME: 'NEWSLETTER_WELCOME',
  ORDER_PLACED: 'ORDER_PLACED',
  ORDER_SHIPPED: 'ORDER_SHIPPED',
  ORDER_DELIVERED: 'ORDER_DELIVERED',
  CHECKOUT_REMINDER: 'CHECKOUT_REMINDER',
  SUPPORT_TICKET_RECEIVED: 'SUPPORT_TICKET_RECEIVED',
  SUPPORT_REPLY: 'SUPPORT_REPLY',
  SUPPORT_STAFF_ALERT: 'SUPPORT_STAFF_ALERT',
} as const;
export type EmailKind = (typeof EmailKind)[keyof typeof EmailKind];

export interface EmailKindInfo {
  kind: EmailKind;
  label: string;
  /** when it goes out, in the words shown to staff */
  when: string;
  /** marketing mail carries an unsubscribe link; order updates do not */
  marketing: boolean;
}

export const EMAIL_KINDS: EmailKindInfo[] = [
  {
    kind: 'NEWSLETTER_WELCOME',
    label: 'Newsletter welcome',
    when: 'Someone adds their email to "notify me" (the Join box in the footer or on the home page).',
    marketing: true,
  },
  {
    kind: 'ORDER_PLACED',
    label: 'Order placed',
    when: 'An order is confirmed: a cash-on-delivery order is placed, or an online payment goes through. Sent only if the customer gave an email.',
    marketing: false,
  },
  {
    kind: 'ORDER_SHIPPED',
    label: 'Order shipped',
    when: 'You mark items as shipped (one email per shipment, with the courier and tracking number).',
    marketing: false,
  },
  {
    kind: 'ORDER_DELIVERED',
    label: 'Order delivered',
    when: 'You set the order to Delivered.',
    marketing: false,
  },
  {
    kind: 'CHECKOUT_REMINDER',
    label: 'Left at checkout',
    when: 'A customer opened checkout but did not finish, or cancelled the payment (see Admin > Checkout for the timing). Sent only if they gave an email, and never to someone who unsubscribed.',
    marketing: true,
  },
  {
    kind: 'SUPPORT_TICKET_RECEIVED',
    label: 'Support ticket received',
    when: 'A customer raises a Help & Support ticket from their account: it confirms we have it and links to the chat.',
    marketing: false,
  },
  {
    kind: 'SUPPORT_REPLY',
    label: 'Support reply',
    when: 'A team member replies on a support ticket (not internal notes): it carries the reply and a link to the chat.',
    marketing: false,
  },
  {
    kind: 'SUPPORT_STAFF_ALERT',
    label: 'Support ticket alert (team)',
    when: 'Sent to the team: to a team member when a ticket is assigned to them or its customer replies, and to a department\'s inbox when a ticket is routed there.',
    marketing: false,
  },
];

export const emailKindInfo = (kind: string) => EMAIL_KINDS.find((k) => k.kind === kind);

const EMAIL_KIND_KEYS = Object.values(EmailKind) as [EmailKind, ...EmailKind[]];

export const emailKindParamSchema = z.object({ kind: z.enum(EMAIL_KIND_KEYS) });
export const emailToggleSchema = z.object({ enabled: z.boolean() });
export const emailTestSchema = z.object({
  to: z.string().trim().toLowerCase().email('Enter a valid email address').max(254),
  /** send this email's sample instead of the plain "email is working" message */
  kind: z.enum(EMAIL_KIND_KEYS).optional(),
});

export interface EmailSettingsDTO {
  status: {
    provider: 'smtp' | 'console';
    host: string;
    port: number;
    secure: boolean;
    from: string;
    replyTo: string | null;
    configured: boolean;
    problem: string | null;
  };
  kinds: (EmailKindInfo & { enabled: boolean })[];
}

export interface EmailPreviewDTO {
  subject: string;
  html: string;
  text: string;
}
